import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Megaphone, SearchX } from "lucide-react";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { EventCard } from "@/components/EventCard";
import { ReminderModal } from "@/components/ReminderModal";
import { OrganizerModal } from "@/components/OrganizerModal";
import { AlertSignup } from "@/components/AlertSignup";
import { FAQ } from "@/components/FAQ";
import { Footer } from "@/components/Footer";
import { fetchEvents } from "@/lib/api";
import { fmtShort } from "@/lib/format";

const CATEGORIES = [
  { id: "alles", label: "Alles" },
  { id: "festival", label: "Festivals" },
  { id: "concert", label: "Concerten" },
  { id: "sport", label: "Sport & Fit" },
  { id: "club", label: "Raves & Indoor" },
  { id: "voetbal", label: "Voetbal" },
];

const TABS = [
  { id: "binnenkort", label: "Binnenkort in verkoop" },
  { id: "live", label: "Nu Live!" },
  { id: "verwacht", label: "Verwacht" },
  { id: "afgelopen", label: "Afgelopen" },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("binnenkort");
  const [cat, setCat] = useState("alles");
  const [query, setQuery] = useState("");
  const [remindEvent, setRemindEvent] = useState(null);
  const [organizerOpen, setOrganizerOpen] = useState(false);

  useEffect(() => {
    fetchEvents()
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const c = { binnenkort: 0, live: 0, verwacht: 0, afgelopen: 0 };
    events.forEach((e) => {
      if (cat !== "alles" && e.category !== cat) return;
      if (c[e.status] !== undefined) c[e.status] += 1;
    });
    return c;
  }, [events, cat]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      if (e.status !== tab) return false;
      if (tab === "afgelopen" && e.archief_oud) return false;
      if (cat !== "alles" && e.category !== cat) return false;
      if (
        q &&
        !`${e.name} ${e.artist || ""} ${e.city} ${e.venue}`.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [events, tab, cat, query]);

  const nextDrop = useMemo(() => {
    const upcoming = events
      .filter((e) => e.status === "binnenkort" && e.sale_start)
      .sort((a, b) => new Date(a.sale_start) - new Date(b.sale_start));
    return upcoming[0] || null;
  }, [events]);

  const marqueeItems = useMemo(
    () =>
      events
        .filter((e) => e.status === "live" || e.status === "binnenkort")
        .map((e) => `${e.name} · ${fmtShort(e.sale_start)}`),
    [events]
  );

  return (
    <div id="top" className="relative min-h-screen bg-obsidian text-white">
      <Header onOrganizer={() => setOrganizerOpen(true)} />
      <Hero
        total={events.filter((e) => e.status !== "afgelopen").length}
        liveCount={counts.live}
        nextDrop={nextDrop}
        onSearch={setQuery}
      />
      <Marquee items={marqueeItems} />

      <main id="events" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 scroll-mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
            De kaartverkoop-agenda
          </p>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Wanneer start de verkoop?
          </h2>
        </motion.div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              data-testid={`category-filter-${c.id}`}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-all ${
                cat === c.id
                  ? "bg-volt text-black shadow-[0_0_24px_rgba(0,230,118,0.35)]"
                  : "border border-white/15 text-slate-300 hover:border-volt/50 hover:text-volt"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2 border-b border-white/10 pb-4" data-testid="status-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`status-tab-${t.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-all ${
                tab === t.id
                  ? "bg-white text-black"
                  : "bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white"
              }`}
            >
              {t.id === "live" && (
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF3B30] opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF3B30]" />
                </span>
              )}
              {t.label}
              <span
                className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] ${
                  tab === t.id ? "bg-black/10" : "bg-white/10"
                }`}
              >
                {counts[t.id]}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-96 animate-pulse rounded-2xl border border-white/[0.06] bg-[#12121C]" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="events-empty-state"
              className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-white/15 py-20 text-center"
            >
              <SearchX className="h-8 w-8 text-slate-600" />
              <p className="text-slate-400">Geen events gevonden voor deze selectie.</p>
              <button
                onClick={() => {
                  setQuery("");
                  setCat("alles");
                }}
                className="text-sm font-semibold text-volt hover:underline"
                data-testid="events-reset-filters-button"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="events-grid">
              {filtered.map((e, i) => (
                <EventCard key={e.id} event={e} index={i} onRemind={setRemindEvent} />
              ))}
            </div>
          )}
        </div>
      </main>

      <AlertSignup />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#12121C] to-[#0D0D12] p-8 sm:p-14"
          data-testid="organizer-cta"
        >
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blaze/15 blur-[100px]" />
          <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-xl">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blaze">
                Voor organisatoren
              </p>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                Organiseer je een event?
              </h2>
              <p className="mt-3 text-slate-400">
                Zet je kaartverkoop in de spotlight bij duizenden bezoekers die niks willen missen.
                Aanmelden is gratis en zo geregeld.
              </p>
            </div>
            <button
              onClick={() => setOrganizerOpen(true)}
              data-testid="organizer-cta-button"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-blaze px-8 py-4 text-sm font-bold text-black transition-transform hover:scale-105 hover:bg-blaze-hover"
            >
              <Megaphone className="h-4 w-4" />
              Meld je kaartverkoop aan
            </button>
          </div>
        </motion.div>
      </section>

      <FAQ />

      <Footer onOrganizer={() => setOrganizerOpen(true)} />

      <ReminderModal event={remindEvent} open={!!remindEvent} onClose={() => setRemindEvent(null)} />
      <OrganizerModal open={organizerOpen} onClose={() => setOrganizerOpen(false)} />
    </div>
  );
}
